import { Modal, message, Input, Spin, Image, Tooltip } from "antd"
import React, { useState, useEffect } from "react"
import { getUserCookies } from "../../../utils/Cookies"
import { Store } from "../../../Apis/storeApis"
import { jwtDecode } from "jwt-decode"
import { InfoCircleOutlined, LinkOutlined } from "@ant-design/icons"
import "./uploadImagesModal.css"
import { translations } from "../../../utils/Translation/staticTranslation"

const StoreViewDetailsModal = ({
  setStores,
  visible,
  onClose,
  selectedStore,
  selectedStatus,
  isReadOnly: isReadOnlyProp,
}) => {
  const [loading, setLoading] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectComment, setRejectComment] = useState("")
  const [requestLoading, setRequestLoader] = useState(false)
  const [storeDetails, setStoreDetails] = useState(null)
  const [previewImage, setPreviewImage] = useState(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewTitle, setPreviewTitle] = useState("")

  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const market_role = decoded?.market_role
  const isReadOnlyFromJwt = market_role === "2" || market_role === "3"
  const isReadOnly =
    typeof isReadOnlyProp === "boolean" ? isReadOnlyProp : isReadOnlyFromJwt

  const lang = localStorage.getItem("language") || "ar"
  const createStore = translations[lang]?.StorePage?.createStore
  const store = translations[lang]?.StorePage
  const btn = translations[lang]?.common_btn
  const common_error_msg = translations[lang]?.common_error_msg

  useEffect(() => {
    if (!visible || isReadOnly) {
      setShowRejectModal(false)
      setRejectComment("")
    }
  }, [visible, isReadOnly])

  useEffect(() => {
    if (selectedStore && visible) {
      setLoading(true)

      // Fetch detailed store information
      const fetchStoreDetails = async () => {
        try {
          let response
          if (selectedStatus === "approved") {
            response = await Store.getApprovedStoreById(selectedStore.id)
          } else {
            response = await Store.getRejectedStoreById(selectedStore.id)
          }

          if (response.status === 200) {
            setStoreDetails(response.data)
          }
        } catch (error) {
          console.error("Error fetching store details:", error)
          message.error(common_error_msg?.failed_to_load_store)
        } finally {
          setLoading(false)
        }
      }

      fetchStoreDetails()
    }
  }, [selectedStore, visible, selectedStatus])

  const handleApproveRequest = async () => {
    if (isReadOnly) return
    setRequestLoader(true)
    const payload = {}
    try {
      const response = await Store.approveRequest(selectedStore.id, payload)
      if (response.status === 200) {
        message.success(store?.approveRequestStore)
        setStores((prevStores) =>
          prevStores.filter((store) => store.id !== selectedStore.id)
        )
        onClose()
      }
    } catch (error) {
      console.error("Error:", error)
      message.error(common_error_msg?.failed_to_approve_request)
    } finally {
      setRequestLoader(false)
    }
  }

  const handleOpenRejectModal = () => {
    if (isReadOnly) return
    setShowRejectModal(true)
  }

  const handleRejectRequest = async () => {
    if (isReadOnly) return
    if (!rejectComment.trim()) {
      message.error(store?.rejection_comment)
      return
    }
    const formData = new FormData()
    formData.append("comment", rejectComment)

    try {
      setRequestLoader(true)
      const response = await Store.rejectRequest(selectedStore.id, formData)

      if (response.status === 200) {
        message.success(store?.request_rejected_msg)
        setStores((prevStores) =>
          prevStores.filter((store) => store.id !== selectedStore.id)
        )
        onClose()
        setShowRejectModal(false)
        setRejectComment("")
      }
    } catch (error) {
      console.error("Error:", error)
      message.error(common_error_msg?.failed_to_reject_request)
    } finally {
      setRequestLoader(false)
    }
  }

  const openUrl = (url) => {
    if (url) {
      window.open(url, "_blank")
    }
  }

  const handlePreview = (url, title) => {
    if (!url) {
      message.info(common_error_msg?.no_document_available)
      return
    }
    setPreviewImage(url)
    setPreviewTitle(title || "Document Preview")
    setPreviewVisible(true)
  }

  const handlePreviewClose = () => {
    setPreviewVisible(false)
  }

  return (
    <div>
      {/* Main Modal */}
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width="80%"
        className="store-details-modal"
        centered
      >
        {loading ? (
          <div className="spinner-container">
            <Spin size="large" />
          </div>
        ) : (
          <div className="store-details-container">
            {storeDetails && (
              <>
                <div className="section">
                  <h3 className="section-title">
                    {createStore?.general_details}
                  </h3>
                  <table className="details-table">
                    <tbody>
                      <tr>
                        <td className="label-cell">
                          {createStore?.vendor_name}
                        </td>
                        <td className="value-cell">
                          {(lang === "ar"
                            ? storeDetails.vendor_name_arabic
                            : storeDetails.vendor_name) || "N/A"}
                        </td>
                        <td className="label-cell">
                          {createStore?.phone_number}
                        </td>
                        <td className="value-cell">
                          {storeDetails.phone_number || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">
                          {createStore?.store_name}
                        </td>
                        <td className="value-cell">
                          {(lang === "ar"
                            ? storeDetails.store_name_arabic
                            : storeDetails.store_name) || "N/A"}
                        </td>
                        <td className="label-cell">
                          {createStore?.store_type}
                        </td>
                        <td className="value-cell">
                          {storeDetails.provider ||
                            storeDetails.store_type ||
                            "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">
                          {createStore?.store_arabic_name}
                        </td>
                        <td className="value-cell">
                          {storeDetails.store_name_arabic || "N/A"}
                        </td>
                        <td className="label-cell">
                          {createStore?.store_logo}
                        </td>
                        <td className="value-cell">
                          {storeDetails.logo ? (
                            <div className="doc-container">
                              <div className="doc-info">
                                <Image
                                  src="/icons/document-icon.svg"
                                  alt="Document"
                                  width={20}
                                  preview={false}
                                  className="doc-icon"
                                />
                                <span className="doc-name">Projectdoc.png</span>
                              </div>
                              <button
                                className="view-btn"
                                onClick={() =>
                                  handlePreview(storeDetails.logo, "Store Logo")
                                }
                              >
                                {createStore?.view}
                              </button>
                            </div>
                          ) : (
                            <div className="doc-container">
                              {createStore?.no_file_uploaded}
                            </div>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">
                          {createStore?.store_description}
                        </td>
                        <td
                          className="value-cell"
                          colSpan="3"
                          style={{
                            width: "83.4%",
                            minHeight: "80px",
                            alignItems: "flex-start",
                            whiteSpace: "normal",
                            lineHeight: "1.5",
                          }}
                        >
                          {(lang === "ar"
                            ? storeDetails?.description_arabic
                            : storeDetails.store_description) || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="section">
                  <h3 className="section-title">
                    {createStore?.business_details}
                  </h3>

                  <table className="details-table">
                    <tbody>
                      <tr>
                        <td className="label-cell">
                          {createStore?.CRN}
                          <Tooltip title={createStore?.registration_number}>
                            <InfoCircleOutlined className="info-icon" />
                          </Tooltip>
                        </td>
                        <td className="value-cell">
                          {storeDetails.registration_number || "27263829832"}
                        </td>
                        <td className="label-cell">
                          {createStore?.BRC}
                          <Tooltip title={createStore?.certificate}>
                            <InfoCircleOutlined className="info-icon" />
                          </Tooltip>
                        </td>
                        <td className="value-cell">
                          {storeDetails.registration_certificate ? (
                            <div className="doc-container">
                              <div className="doc-info">
                                <Image
                                  src="/icons/document-icon.svg"
                                  alt="Document"
                                  width={20}
                                  preview={false}
                                  className="doc-icon"
                                />
                                <span className="doc-name">Projectdoc.png</span>
                              </div>
                              <button
                                className="view-btn"
                                onClick={() =>
                                  handlePreview(
                                    storeDetails.registration_certificate,
                                    "Registration Certificate"
                                  )
                                }
                              >
                                {createStore?.view}
                              </button>
                            </div>
                          ) : (
                            createStore?.no_file_uploaded
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">
                          {createStore?.tax_number}
                        </td>
                        <td className="value-cell">
                          {storeDetails.vat_number || "27263829832"}
                        </td>
                        <td className="label-cell">
                          {createStore?.vat_certificate}
                        </td>
                        <td className="value-cell">
                          {storeDetails.vat_certificate ? (
                            <div className="doc-container">
                              <div className="doc-info">
                                <Image
                                  src="/icons/document-icon.svg"
                                  alt="Document"
                                  width={20}
                                  preview={false}
                                  className="doc-icon"
                                />
                                <span className="doc-name">Projectdoc.png</span>
                              </div>
                              <button
                                className="view-btn"
                                onClick={() =>
                                  handlePreview(
                                    storeDetails.vat_certificate,
                                    "VAT Certificate"
                                  )
                                }
                              >
                                {createStore?.view}
                              </button>
                            </div>
                          ) : (
                            createStore?.no_file_uploaded
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">
                          {createStore?.business_url}
                        </td>
                        <td className="value-cell">
                          {storeDetails.business_url ? (
                            <div className="link-container">
                              <LinkOutlined className="link-icon" />
                              <button
                                className="link-btn"
                                onClick={() =>
                                  openUrl(storeDetails.business_url)
                                }
                              >
                                {storeDetails.business_url}
                              </button>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="label-cell">
                          {createStore?.maroof_link}
                        </td>
                        <td className="value-cell">
                          {storeDetails.maroof_link ? (
                            <div className="link-container">
                              <LinkOutlined className="link-icon" />
                              <button
                                className="link-btn"
                                onClick={() =>
                                  openUrl(storeDetails.maroof_link)
                                }
                              >
                                {storeDetails.maroof_link || "N/A"}
                              </button>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="label-cell">{createStore?.location}</td>
                        <td className="value-cell">
                          {storeDetails.location ? (
                            <div className="link-container">
                              <LinkOutlined className="link-icon" />
                              <button
                                className="link-btn"
                                onClick={() => openUrl(storeDetails.location)}
                              >
                                {storeDetails.location || "N/A"}
                              </button>
                            </div>
                          ) : (
                            "Maroof Link"
                          )}
                        </td>
                        <td className="label-cell">
                          {createStore?.country_of_sale}
                        </td>
                        <td className="value-cell">
                          {Array.isArray(storeDetails.countries)
                            ? storeDetails.countries.join(", ")
                            : storeDetails.countries || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {selectedStatus === "pending" && (
                  <div className="action-buttons">
                    <button
                      type="button"
                      className="reject-btn"
                      onClick={handleOpenRejectModal}
                      disabled={isReadOnly || requestLoading}
                    >
                      {btn.reject}
                    </button>
                    <button
                      type="button"
                      className="accept-btn"
                      onClick={handleApproveRequest}
                      disabled={isReadOnly || requestLoading}
                    >
                      {btn.accept}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={previewVisible}
        // title={previewTitle}
        footer={null}
        onCancel={handlePreviewClose}
        className="preview-modal"
        width="500px"
        style={{ minHeight: "100px" }}
        centered
      >
        <div className="preview-container">
          <Image
            alt={previewTitle}
            width="auto"
            style={{ margin: "auto" }}
            src={previewImage}
            preview={false}
            placeholder={
              <div className="image-loading-container">
                <Spin size="large" />
              </div>
            }
          />
        </div>
      </Modal>

      <Modal
        title={store?.reject_comment_modal?.title}
        open={showRejectModal}
        onCancel={() => setShowRejectModal(false)}
        footer={null}
        className="rejection-modal"
        centered
      >
        <Input.TextArea
          className="rejection-textarea"
          rows={4}
          placeholder={store?.reject_comment_modal?.placeholder}
          value={rejectComment}
          onChange={(e) => setRejectComment(e.target.value)}
        />
        <div className="rejection-buttons">
          <button
            className="cancel-btn"
            onClick={() => {
              setShowRejectModal(false)
              setRejectComment("")
            }}
          >
            {btn?.cancel_btn}
          </button>
          <button
            type="button"
            className="confirm-reject-btn"
            onClick={handleRejectRequest}
            disabled={isReadOnly || requestLoading}
          >
            {btn?.reject}
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default StoreViewDetailsModal
